/**
 * Quick Answers container
 * 1. Default case: show topic tree selection and topic title for quick answers
 * Call us page: HELPHUB_TOPCONTACTREASON
 * Email Page: faq topic externalized in remote config under webform_faq_topic_mapping
 *
 * 2. User make selection: show topic selection without title
 * until user reach to the end of topic tree
 *
 * 3. Make sure:
 *  1) the key for layers are layer1, layer2
 *  2) the topic ID in the Layer 1 is different with Layer 2
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _isString from 'lodash/isString';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

// Components and Helper
import Select from 'kepler/lib/components/Select';
import RelatedArticles from '../components/RelatedArticles';
import Page from '../../lib/page';

// Actions
import { selectQuickAnswer } from '../actions/action-quick-answer-topics';

// Lib
import i18n from '../../lib/reacti18n';
import { trackLink } from '../../lib/analytics-helper';

// Constants
import { QUICKANSWERS, QUICKANSWERS_SELECTED, RELATED_ARTICLES, TOP_REASON_TOPICS } from '../constants/PropTypesShapes';
import { limit, quickAnswerLayer } from '../constants/UtilVariables';
import { isPrivacyWebFormEnabledInRC } from '../../lib/globalHelper';

const quickAnswers = {
  header: i18n.message('smarthelp/quickAnswers.quickAnswers.header'),
  placeholder: {
    layer1: i18n.message('smarthelp/quickAnswers.quickAnswers.topicPlaceHolder.layer1'),
    layer2: i18n.message('smarthelp/quickAnswers.quickAnswers.topicPlaceHolder.layer2'),
  },
  noFaq: i18n.message('smarthelp/quickAnswers.quickAnswers.noFaq'),
};

class QuickAnswer extends Component {
  // Render Suggest topic List
  static renderTopicList(options) {
    return QuickAnswer.renderRelatedTopics(options);
  }

  // To valid not empty array
  static arrayValidation(array) {
    return Array.isArray(array) && array.length > 0;
  }

  /**
   * Parse topic selection to getting layer number and topic id
   * @param  {string} selectedId  'layer|topicId'
   * @return {object}             { layer, topicId }
   */
  static parseSelection({ selectedId }) {
    if (selectedId && _isString(selectedId)) {
      const select = selectedId.split('|');
      if (Array.isArray(select) && select.length > 0) {
        return {
          layer: select[0],
          value: select[1],
        };
      }
    }
    return selectedId;
  }

  // Parser layer 1 and 2 topic Id and name
  static parseTopicInfo({ layer, input }) {
    if (layer === quickAnswerLayer.layer1) {
      return {
        topicId: input.topicId,
        topicName: input.topicName,
      };
    }
    if (layer === quickAnswerLayer.layer2) {
      return {
        topicId: input.subTopicId,
        topicName: input.subTopicName,
      };
    }
    return {};
  }

  // Select Box data parser; Put the layer info into the value for Click Handle know with layer
  static parseSelectBoxOptions({ inputs, key }) {
    if (QuickAnswer.arrayValidation(inputs)) {
      return inputs.map((input) => {
        const tempParseInput = QuickAnswer.parseTopicInfo({ layer: key, input }); // Parse topic tree data based on layer
        return {
          value: Page.formalQuickAnswerKey({ layer: key, topicId: tempParseInput.topicId }),
          label: tempParseInput.topicName,
        };
      });
    }
    return [];
  }

  // Default Help Top contact reason Data Parser
  static parseTopReasonTopics(topics) {
    if (QuickAnswer.arrayValidation(topics)) {
      return topics.map((topic) => ({
        articleTitle: topic.articleTitle,
        articleId: topic.articleId,
      }));
    }
    return [];
  }

  // Render Select Box for each layer selection
  static renderSelectBox({ value, options, placeholder, label, changeHandler, className }) {
    return (
      <Select
        value={value}
        options={options}
        onChange={changeHandler}
        label={label}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  // To display related topics with RelatedArticles Component
  static renderRelatedTopics(options) {
    if (QuickAnswer.arrayValidation(options.topicList)) {
      return (
        <div className="quick-answer-topic-list">
          <RelatedArticles
            relatedArticles={options.topicList}
            displayNumber={limit.quickAnswers}
            FPTIPageName={options.pageName && `${options.pageName}Page`}
          />
        </div>
      );
    }
    return null;
  }

  constructor(props) {
    super(props);

    this.changeHandlerSelectBox = this.changeHandlerSelectBox.bind(this);
  }

  /**
   * getTopicSuggestion get related topic for Find Quick Answer
   * Condition:
   *  1. When neither layer 1 nor 2 selected: return TOP REASON as suggested topics
   *  2. When layer 1 selected, but layer 2 not selected: return emptry array
   *  3. When both layer 1 and 2 selected: return Layer 2 web topic response as suggest topics
   * @return {array}    suggest topic list
   */
  getTopicSuggestion() {
    let topicList = [];
    if (!this.props.selectedTopics.layer1 && !this.props.selectedTopics.layer2) {
      topicList = QuickAnswer.parseTopReasonTopics(this.props.suggestion);
    } else if (this.props.selectedTopics.layer1 && this.props.selectedTopics.layer2 && !this.props.topicsLoading) {
      topicList = this.getLayer2ReceivedTopics();
    }
    return topicList;
  }

  // Get suggest topics, when user seleted layer 2 in the quick answer
  getLayer2ReceivedTopics() {
    const layerInfo = QuickAnswer.parseSelection({ selectedId: this.props.selectedTopics.layer2 });

    if (layerInfo && layerInfo.value && this.props.answers && QuickAnswer.arrayValidation(this.props.answers.layer2)) {
      // Using Topic Id to find selected object
      const result = _find(this.props.answers.layer2, (answer) => answer.subTopicId === layerInfo.value);
      if (result && QuickAnswer.arrayValidation(result.articles)) {
        return result.articles;
      }
    }
    return [];
  }

  // Return select box css class
  getFaqBoxClassName({ key }) {
    return classNames({
      'faq-select-box': true,
      selected: !!this.props.selectedTopics[key],
    });
  }

  // Check whether the layer 2 data is loading or not
  isNotLoading({ key }) {
    return key !== quickAnswerLayer.layer2 || !this.props.topicsLoading;
  }

  // Find quick Answer display logic; hide the section when there is no topic tree content
  isAnswerExist() {
    return this.props.answers && QuickAnswer.arrayValidation(this.props.answers.layer1);
  }

  // Handle Select Box changed
  changeHandlerSelectBox(selectedId) {
    const answerSelection = QuickAnswer.parseSelection({ selectedId });
    if (answerSelection && answerSelection.value && answerSelection.layer) {
      this.props.selectQuickAnswer({ layer: answerSelection.layer, topicId: answerSelection.value });
    }

    if (this.props.pageName) {
      const link = `smarthelp|find-quick-answers|${selectedId}`;
      // Expect pageName: callUs/Email;
      // TODO: Check page-name-identifers and update page name
      trackLink({ id: `${this.props.pageName}Page`, link });
    }
  }

  // Render layer 1 and layer 2 Selection box
  renderSelectSection() {
    if (!_isEmpty(this.props.answers)) {
      const layerAnswers = Object.keys(this.props.answers);
      if (QuickAnswer.arrayValidation(layerAnswers)) {
        return layerAnswers.map((key) => {
          const options = QuickAnswer.parseSelectBoxOptions({ inputs: this.props.answers[key], key });

          // Changing the width of quick answers dropdown for new privacy page
          const quickAnswersClassName = isPrivacyWebFormEnabledInRC(this.props.kanaCode)
            ? 'quick-answer-selection new-privacy-page'
            : 'quick-answer-selection';

          return QuickAnswer.arrayValidation(options) && this.isNotLoading({ key }) ? (
            <div key={key} className={`${quickAnswersClassName}`}>
              {QuickAnswer.renderSelectBox({
                value: this.props.selectedTopics[key],
                options,
                label: quickAnswers.placeholder[key],
                placeholder: quickAnswers.placeholder[key],
                changeHandler: this.changeHandlerSelectBox,
                className: this.getFaqBoxClassName({ key }),
              })}
            </div>
          ) : null;
        });
      }
    }
    return null;
  }

  // add message when no faq available, remove it once any L1 topics selected
  renderNoFaqMsg() {
    if (!this.props.selectedTopics.layer1 && !this.props.selectedTopics.layer2 && this.props.suggestion.length === 0) {
      return <div className="no-selection">{quickAnswers.noFaq}</div>;
    }
    return null;
  }

  renderQuickAnswers() {
    const { isIntentUpdated, pageName, relatedArticles } = this.props;
    if (!isIntentUpdated) {
      return (
        <Fragment>
          <p className="quick-answer-header">{quickAnswers.header}</p>
          <div className="quick-answer-content">
            {this.renderSelectSection()}
            {QuickAnswer.renderTopicList({ topicList: this.getTopicSuggestion(), pageName })}
            {this.renderNoFaqMsg()}
          </div>
        </Fragment>
      );
    }

    if (Array.isArray(relatedArticles) && relatedArticles.length) {
      return (
        <Fragment>
          <p className="quick-answer-header">{quickAnswers.header}</p>
          <div className="quick-answer-content">
            {QuickAnswer.renderTopicList({ topicList: relatedArticles, pageName })}
          </div>
        </Fragment>
      );
    }

    return null;
  }

  // Render Quick Answers Component
  render() {
    if (!this.isAnswerExist()) {
      return null;
    }
    return <div className="quick-answer-container">{this.renderQuickAnswers()}</div>;
  }
}

// QuickAnswer PropTypes
QuickAnswer.propTypes = {
  answers: PropTypes.shape(QUICKANSWERS),
  selectedTopics: PropTypes.shape(QUICKANSWERS_SELECTED),
  suggestion: PropTypes.arrayOf(PropTypes.shape(TOP_REASON_TOPICS)),
  selectQuickAnswer: PropTypes.func.isRequired,
  topicsLoading: PropTypes.bool,
  pageName: PropTypes.string,
  isIntentUpdated: PropTypes.bool,
  relatedArticles: PropTypes.shape(RELATED_ARTICLES),
  kanaCode: PropTypes.string.isRequired,
};

QuickAnswer.defaultProps = {
  answers: {},
  display: true,
  selectedTopics: {},
  suggestion: [],
  topicsLoading: false,
  pageName: 'callUs',
  isIntentUpdated: false,
  relatedArticles: {},
};

// Set props by reducer state
function mapStateToProps(state) {
  return {
    answers: state.quickAnswers.answers,
    display: state.quickAnswers.display,
    selectedTopics: state.quickAnswers.selected, // layer 1 and layer 2 selected topic name
    suggestion: state.suggestTopics.topics,
    topicsLoading: state.quickAnswers.isLoading,
    relatedArticles: state.activeArticle.relatedArticles,
    isIntentUpdated: state.quickAnswers.isIntentUpdated,
    kanaCode: state.emailNoIntent.kanaCode,
  };
}

// Dispatch to Props
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ selectQuickAnswer }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(QuickAnswer);
